import { installedIds } from "@p0security/shared/install/installed";
import type { NewConfig } from "@p0security/shared/integrations/shared";
import {
  type McpServerSpec,
  propagationHint,
} from "@p0security/shared/types/agentic/mcp";

import { dynamoDbMcpTools } from "./mcp-tools.ts";
import type { DynamoDbAiIntegration } from "./types.ts";

const prefixArgs = ["dynamodb-ai", "table"];

// V1 measured 4 to 10 seconds to grant and 1 to 10 seconds to revoke, in five
// runs on 2026-10-27.
const dynamoDbPropagationHint = `${propagationHint({
  maxWait: "30 seconds",
  normalWait: "about 10 seconds",
  pollInterval: "5 seconds",
  resource: "AWS IAM",
})}

If the request fails because the role pool is full, tell the user; an admin must revoke unused access or add roles to the pool.`;

// The IAM actions each tool calls, for the IAM Risk Catalog only: IAM decides
// what each call can do.
const toolPermissions = {
  batch_get_item: ["dynamodb:BatchGetItem"],
  batch_write_item: ["dynamodb:BatchWriteItem"],
  delete_item: ["dynamodb:DeleteItem"],
  describe_table: ["dynamodb:DescribeTable", "dynamodb:DescribeTimeToLive"],
  get_item: ["dynamodb:GetItem"],
  put_item: ["dynamodb:PutItem"],
  query: ["dynamodb:Query"],
  scan: ["dynamodb:Scan"],
  update_item: ["dynamodb:UpdateItem"],
} satisfies Record<string, string[]>;

/**
 * The Amazon DynamoDB server. The gateway assumes the aws-oidc pool role that
 * the grant's statement is in, and runs the image with that role's
 * credentials in the grant's Region.
 */
export const dynamoDbMcp: McpServerSpec & { type: "container" } = {
  type: "container",
  label: "Amazon DynamoDB",
  iconUrl:
    "https://awsfundamentals.com/assets/aws-icons/Arch_Amazon-DynamoDB_64.svg",
  credential: {
    type: "wif",
    csp: "aws",
    parameter: "generated.name",
    // dynamodb-ai delegates straight to aws-oidc, so one hop.
    awsRoleArn: "{{ request.delegation[0].request.generated.arn }}",
  },
  authorization: "upstream",
  transport: "stdio",
  container: {
    command: [],
    args: [],
    envs: {
      // The gateway's AwsTempCreds field names, as in the AWS CLI server.
      AWS_ACCESS_KEY_ID: "{{credential.accessKeyId}}",
      AWS_SECRET_ACCESS_KEY: "{{credential.secretAccessKey}}",
      AWS_SESSION_TOKEN: "{{credential.sessionToken}}",
      AWS_REGION: "{{ request.permission.region }}",
    },
    image: {
      image:
        "p0security/mcp-dynamodb:a7fa721@sha256:b51f96273ba15717f2dc4f80bdb7f98495da3417af4c909bfa2363c2399c03ab",
    },
  },
  tools: { type: "manual", tools: dynamoDbMcpTools },
  toolPermissions,
  access: {
    prompt: `Request DynamoDB table access with argv: [<account>, <region>, <table>, <level>, "--reason", <reason>]
- account: AWS account ID. Use list.
- region: AWS Region of the table, e.g. us-west-2. Use list with account.
- table: table name. The grant covers the table and its indexes. Use list with account and region.
- level: 'read' to describe, get, query and scan. 'write' to also put, update and delete items.
Each grant covers one table in one Region.`,
    prefixArgs,
    propagationHint: dynamoDbPropagationHint,
  },
  list: {
    fields: {
      account: { description: "AWS account ID", required: [] },
      region: { description: "AWS Region", required: ["account"] },
      table: {
        description: "DynamoDB table name",
        required: ["account", "region"],
      },
    },
    prefixArgs,
  },
  location: { key: "account", description: "AWS account ID" },
};

/**
 * The server, keyed by its catalog ID. The key is stored as each added
 * server's `definition.id`, so it must never change.
 */
export const dynamoDbAiMcpServers = { dynamodb: dynamoDbMcp };

/** The server can be added once the tenant has an installed account. */
export const dynamoDbAiMcpServerAvailability = {
  dynamodb: (installed: Record<string, NewConfig>): boolean =>
    installedIds(
      "iam-write",
      (installed["dynamodb-ai"] ?? {}) as DynamoDbAiIntegration
    ).length > 0,
};
